import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { LoopOutlined } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';

import { checkFraud } from '~/services/OrdersService';

import { SingleOrderPayments } from './SingleOrderPayments';

interface Props {
  order: App.Order;
}

function SingleCarHireDetails({ order }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [loadingFraudCheck, setLoadingFraudCheck] = useState<boolean>(false);
  const [recalculatedFraudScore, setRecalculatedFraudScore] = useState<number | undefined>(undefined);
  const [recalculatedFraudDesc, setRecalculatedFraudDesc] = useState<string | undefined>(undefined);

  const formattedTotal = order.total.toLocaleString(undefined, { maximumFractionDigits: 0 });

  const handleReRunFraudCheck = async () => {
    setLoadingFraudCheck(true);
    try {
      const response = await checkFraud(order.id_orders);
      setRecalculatedFraudScore(response.score);
      setRecalculatedFraudDesc(response.fraudDesc);
    } catch (error) {
      setRecalculatedFraudScore(undefined);
      setRecalculatedFraudDesc(undefined);
      enqueueSnackbar('Error fetching recalculated fraud check', { variant: 'error' });
    }
    setLoadingFraudCheck(false);
  };

  const firstCarHireItem = order.car_hire_items[0];
  const reservation = firstCarHireItem?.reservation;

  // Get pick up and return location info
  const pickUpLocation = `${reservation?.pickUp?.locationName}, ${reservation?.pickUp?.countryName}` || 'Unknown';
  const returnLocation = `${reservation?.return?.locationName}, ${reservation?.return?.countryName}` || 'Unknown';
  const pickUpDate = reservation?.pickUp?.date;
  const returnDate = reservation?.return?.date as string;

  // Calculate lead days from order creation to pick up date
  let leadDays = 999;
  if (pickUpDate) {
    leadDays = Math.floor(
      (new Date(pickUpDate).setHours(0, 0, 0, 0) - new Date(order.created_at).setHours(0, 0, 0, 0)) /
        (1000 * 60 * 60 * 24),
    );
  }

  // Calculate rental duration
  let rentalDays = 0;
  if (pickUpDate && returnDate) {
    rentalDays = Math.floor(
      (new Date(returnDate).setHours(0, 0, 0, 0) - new Date(pickUpDate).setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24),
    );
  }

  // Get driver info from reservation if available
  const driverInfo = reservation?.driver;
  const driverName = driverInfo ? `${driverInfo.givenName || ''} ${driverInfo.surname || ''}`.trim() : null;
  const driverEmail = driverInfo?.email;
  const driverCountry = driverInfo?.countryCode;

  // Check if driver name matches account holder
  const accountName = order.customer_full_name?.toLowerCase().trim();
  const driverNameLower = driverName?.toLowerCase().trim();
  const namesMatch = accountName && driverNameLower && accountName === driverNameLower;

  return (
    <Box>
      <a target="_blank" href={`/purchases/${order.id}`} rel="noreferrer">
        car_hire
      </a>{' '}
      for {order.currency_code}
      {formattedTotal}{' '}
      {order.fk_purchaser_id && order.fk_purchaser_id !== order.fk_customer_id && <strong>Spoofed Order</strong>}
      <br />
      <strong>Account:</strong> {order.customer_full_name} | {order.customer_email} | {order.customer_phone_prefix}
      {order.customer_phone}
      <br />
      {driverName && (
        <>
          <strong>Driver:</strong> {driverName}
          {namesMatch && (
            <Tooltip title="Driver name matches account holder">
              <span> 🟢</span>
            </Tooltip>
          )}
          {!namesMatch && accountName && driverNameLower && (
            <Tooltip title="Driver name is different from account holder - possible gift or fraud risk">
              <span> 🟠</span>
            </Tooltip>
          )}
          {driverEmail && driverEmail !== order.customer_email && (
            <>
              {' '}
              | {driverEmail}
              <Tooltip title="Driver email is different from account email">
                <span> 🟠</span>
              </Tooltip>
            </>
          )}
          {driverEmail && driverEmail === order.customer_email && (
            <Tooltip title="Driver email matches account email">
              <span> 🟢</span>
            </Tooltip>
          )}
          <br />
        </>
      )}
      <strong>Pick-up:</strong> {pickUpLocation}
      <br />
      <strong>Return:</strong> {returnLocation}
      <br />
      {leadDays}d lead time{' '}
      {leadDays <= 10 && (
        <Tooltip title="Fraudsters book quickly before we can catch them! Booking under 10 days in advance is a risk. Under 3 days is high risk.">
          <span>{leadDays <= 3 ? '🔴' : '🟠'}</span>
        </Tooltip>
      )}
      {leadDays > 10 && (
        <Tooltip title="Longer lead times are more likely to be genuine customers.">
          <span>🟢</span>
        </Tooltip>
      )}
      {rentalDays > 0 && <> | {rentalDays}d rental</>}
      <br />
      <Typography component="span" sx={{ fontStyle: 'italic' }}>
        {order.car_hire_items.length} car hire item{order.car_hire_items.length !== 1 ? 's' : ''}
      </Typography>{' '}
      {driverCountry && <>| Driver country: {driverCountry} </>}
      <Tooltip title="Car hire bookings require the driver to be present with valid ID and credit card at pick-up, adding friction for fraudsters.">
        <span>🟢</span>
      </Tooltip>
      <br />
      <strong>Provider:</strong> {(firstCarHireItem?.reservation?.vendor?.name as string) || 'Unknown'}
      <br />
      <strong>Platform:</strong> {order.platform}
      <br />
      <strong>Fraud Score: </strong>
      <Tooltip title={order.fraud_desc}>
        <span>{order.fraud_score}</span>
      </Tooltip>
      <Tooltip title="Re-run fraud check">
        <IconButton size="small" disabled={loadingFraudCheck} onClick={handleReRunFraudCheck}>
          {loadingFraudCheck ? <CircularProgress size={16} /> : <LoopOutlined />}
        </IconButton>
      </Tooltip>
      <br />
      {loadingFraudCheck && <span>Loading...</span>}
      {recalculatedFraudScore !== undefined && !loadingFraudCheck && (
        <Box>
          <strong>Recalculated Fraud Score: </strong>
          <Tooltip title={recalculatedFraudDesc}>
            <span>{recalculatedFraudScore}</span>
          </Tooltip>
        </Box>
      )}
      <SingleOrderPayments order={order} />
    </Box>
  );
}

export { SingleCarHireDetails };
